import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, defer, map, of, startWith, switchMap } from 'rxjs';
import {
  ApplicationTemplateDto,
  FormApiService,
  QuestionNumberingService,
  QuestionView
} from '@formrails/form-builder/data-access';
import { ApplicationTemplateDto as TemplateDetailDto, ApplicationTemplateMapper } from '@formrails/shared/data-access';
import { QuestionListComponent } from '../question-list/question-list.component';

/** What the pane is showing for the chosen form. */
type CompareState =
  | { kind: 'loading' }
  | { kind: 'loaded'; questions: QuestionView[] }
  | { kind: 'error' };

/** A saved form beside the one being edited: one flat list, so the two sides stay level. */
@Component({
  selector: 'app-form-compare-pane',
  standalone: true,
  imports: [QuestionListComponent],
  templateUrl: './form-compare-pane.component.html',
  styleUrl: './form-compare-pane.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormComparePaneComponent {
  /** The saved form to show. */
  readonly template = input.required<ApplicationTemplateDto>();
  readonly showQuestionNumbers = input(true);

  private formApi = inject(FormApiService);
  private questionNumbering = inject(QuestionNumberingService);

  /** Loads the chosen form; choosing another cancels a load still in flight. */
  private readonly state = toSignal(
    toObservable(computed(() => this.template().id)).pipe(
      switchMap(templateId =>
        // defer() turns a synchronous throw into an error catchError handles.
        defer(() => this.formApi.getFormByTemplateId(templateId)).pipe(
          map(dto => ({ kind: 'loaded', questions: this.toDisplayQuestions(dto) }) as CompareState),
          catchError(() => of<CompareState>({ kind: 'error' })),
          startWith<CompareState>({ kind: 'loading' })
        )
      )
    ),
    { initialValue: { kind: 'loading' } as CompareState }
  );

  readonly loading = computed(() => this.state().kind === 'loading');
  readonly failed = computed(() => this.state().kind === 'error');
  readonly questions = computed(() => {
    const state = this.state();
    return state.kind === 'loaded' ? state.questions : null;
  });

  /** Builder order and numbering, not in edit mode. */
  private toDisplayQuestions(dto: TemplateDetailDto): QuestionView[] {
    const questions = (ApplicationTemplateMapper.toForm(dto).questions ?? []).map(question => ({
      ...question,
      isEditing: false,
      showMenu: false
    }));
    return this.questionNumbering.recalculateQuestionNumbers(questions);
  }
}
